import { trackEvent } from '@saaga/shared/utils/analytics.util'
import { logger } from '@saaga/shared/utils/logger.util'
import { useCallback } from 'react'
import { toast } from 'sonner'

import type { SeatInfo } from '@/components/billing/billing-seat.interface'
import { useOrganizationEarlyAccess } from '@/hooks/use-organization-early-access.hook'

import { useBillingClient } from './use-billing-client.hook'
import { useSeatsApi } from './use-seats-api.hook'

export type RemoveSeatsResult =
  | { success: true }
  | { success: false; reason: 'member_count_exceeded'; currentMemberCount: number }
  | { success: false; reason: 'error'; message: string }

const getErrorMessage = (err: unknown, fallback: string): string =>
  err instanceof Error ? err.message : fallback

interface UseRemoveSeatsParams {
  seatInfo: SeatInfo | null
  setIsRemovingSeats: (isRemoving: boolean) => void
  fetchSeats: () => Promise<void>
}

const buildRemoveSuccessMessage = (quantity: number, earlyAccess: boolean): string => {
  const seatWord = quantity === 1 ? 'seat' : 'seats'
  if (earlyAccess) {
    return `${quantity} ${seatWord} removed`
  }
  return `${quantity} ${seatWord} will be removed at the end of the billing period`
}

export const useRemoveSeats = ({
  seatInfo,
  setIsRemovingSeats,
  fetchSeats,
}: UseRemoveSeatsParams) => {
  const { removeSeats: removeSeatsApi } = useSeatsApi()
  const { refetchTenant } = useBillingClient()
  const { isEarlyAccess } = useOrganizationEarlyAccess()

  const removeSeats = useCallback(
    async (quantity: number, currentMemberCount: number): Promise<RemoveSeatsResult> => {
      if (!seatInfo) {
        return { success: false, reason: 'error', message: 'Seat information not loaded' }
      }

      const seatsAfterRemoval = seatInfo.totalSeats - quantity
      if (seatsAfterRemoval < currentMemberCount) {
        return { success: false, reason: 'member_count_exceeded', currentMemberCount }
      }

      setIsRemovingSeats(true)
      try {
        await removeSeatsApi(quantity)
        toast.success(buildRemoveSuccessMessage(quantity, isEarlyAccess))
        trackEvent('seats_removed', { quantity })
        await Promise.all([fetchSeats(), refetchTenant()])
        return { success: true }
      } catch (err) {
        const message = getErrorMessage(err, 'Failed to remove seats')
        toast.error(message)
        logger.error('Failed to remove seats', { error: err, quantity })
        return { success: false, reason: 'error', message }
      } finally {
        setIsRemovingSeats(false)
      }
    },
    [seatInfo, removeSeatsApi, fetchSeats, refetchTenant, setIsRemovingSeats, isEarlyAccess]
  )

  return { removeSeats }
}
